/**
 * CompassTracker — wraps DeviceOrientationEvent and exposes a simple
 * heading (0-360°, 0=north). Handles the iOS 13+ permission prompt
 * which MUST be requested inside a user gesture (so the caller needs
 * to invoke requestPermission() from a click handler, NOT from init).
 *
 * Emits 'compass:update' on every reading.
 */
export class CompassTracker {
    constructor(_bus) {
        this._bus = _bus;
        this._handler = null;
        this._permissionGranted = false;
    }
    /** Returns true if no permission prompt is required (non-iOS) */
    get permissionsImplicit() {
        const Ctor = DeviceOrientationEvent;
        return typeof Ctor.requestPermission !== 'function';
    }
    /** Request permission (iOS). Must be called from a user gesture. */
    async requestPermission() {
        const Ctor = DeviceOrientationEvent;
        if (typeof Ctor.requestPermission !== 'function') {
            this._permissionGranted = true;
            return true;
        }
        try {
            const result = await Ctor.requestPermission();
            this._permissionGranted = result === 'granted';
            return this._permissionGranted;
        }
        catch {
            this._permissionGranted = false;
            return false;
        }
    }
    /** Start listening to device orientation events */
    start() {
        if (this._handler)
            return;
        // On non-iOS we can start without permission flow
        if (!this._permissionGranted && !this.permissionsImplicit) {
            // Permission not yet granted — caller must requestPermission() first
            return;
        }
        this._handler = (ev) => {
            // iOS: webkitCompassHeading is in the non-standard extension
            const webkitHeading = ev.webkitCompassHeading;
            let heading = null;
            if (typeof webkitHeading === 'number') {
                heading = webkitHeading;
            }
            else if (typeof ev.alpha === 'number') {
                // Convert alpha (counter-clockwise from north) to clockwise compass heading
                heading = (360 - ev.alpha) % 360;
            }
            if (heading !== null) {
                this._bus.emit('compass:update', { heading });
            }
        };
        window.addEventListener('deviceorientation', this._handler);
    }
    stop() {
        if (this._handler) {
            window.removeEventListener('deviceorientation', this._handler);
            this._handler = null;
        }
    }
}
//# sourceMappingURL=CompassTracker.js.map